import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { API_BASE } from "@/services/api";
import { Loader2, AlertCircle } from "lucide-react";

/**
 * Direct Google OAuth return handler.
 * Google redirects here as `/auth/google?code=...&state=...`. We validate the
 * state, exchange the code via our backend for the app JWT, then continue.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const GoogleDirectCallback = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const expectedState = sessionStorage.getItem("google_oauth_state");
    sessionStorage.removeItem("google_oauth_state");

    if (params.get("error")) {
      setError("Google sign-in was cancelled. Please try again.");
      setTimeout(() => navigate("/auth"), 2000);
      return;
    }
    if (!code) {
      setError("No authorization code was returned. Please try again.");
      setTimeout(() => navigate("/auth"), 2000);
      return;
    }
    if (expectedState && state !== expectedState) {
      setError("Sign-in security check failed. Please try again.");
      setTimeout(() => navigate("/auth"), 2000);
      return;
    }

    (async () => {
      try {
        const redirectUri = window.location.origin + "/auth/google";
        const { data } = await axios.post(`${API_BASE}/auth/google/code`, {
          code,
          redirect_uri: redirectUri,
        });
        localStorage.setItem("token", data.token.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setAuth({ user: data.user, token: data.token.access_token });
        window.history.replaceState(null, "", window.location.pathname);
        // Returning users skip the one-time exam selector → straight to dashboard.
        if (!data.is_new_user) {
          const uid = data.user.id;
          if (!localStorage.getItem(`clarias_exam_${uid}`)) {
            localStorage.setItem(`clarias_exam_${uid}`, "UPSC");
          }
          localStorage.setItem(`clarias_exam_selector_v2_${uid}`, "true");
        }
        navigate("/");
      } catch (err) {
        const msg = err?.response?.data?.detail || "Google sign-in failed. Please try again.";
        setError(typeof msg === "string" ? msg : "Google sign-in failed. Please try again.");
        setTimeout(() => navigate("/auth"), 2500);
      }
    })();
  }, [navigate, setAuth]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220] flex items-center justify-center px-4"
      data-testid="google-direct-callback"
    >
      <div className="text-center space-y-4">
        {error ? (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
            <p className="text-slate-200 max-w-sm" data-testid="google-direct-error">{error}</p>
            <p className="text-slate-500 text-sm">Redirecting you back to sign-in…</p>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-orange-400 mx-auto animate-spin" />
            <p className="text-slate-200">Signing you in with Google…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleDirectCallback;
